import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { prefetchShow } from '../lib/prefetch';
import { useCatalog } from '../lib/catalog';
import { useQuery } from '../lib/query';
import { navigate, useLocation } from '../lib/router';
import { useDesktop } from '../lib/useMedia';
import { Icon } from '../components/Icon';
import { PosterLink } from '../components/Poster';
import { AppShell, ErrorBox, SkeletonRails } from '../components/Shell';

export function Explore({ topic }: { topic?: string }) {
  const { topics: TOPICS } = useCatalog();
  const { query } = useLocation();
  const desktop = useDesktop();
  const q = query.get('q') || '';
  const [text, setText] = useState(q);
  useEffect(() => setText(q), [q]);
  const current = topic || 'govt';
  const t = TOPICS.find((x) => x.slug === current);

  const shows = useQuery(q ? 'search:' + q : 'topic:' + current, () => (q ? api.search(q) : api.showsByTopic(current)));
  // Show details are small: fetch the first few in the background so tapping one opens instantly.
  useEffect(() => {
    const list = shows.data?.slice(0, 6) ?? [];
    if (!list.length) return;
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback ?? ((cb: () => void) => window.setTimeout(cb, 300));
    idle(() => list.forEach((s) => prefetchShow(s.slug)));
  }, [shows.data]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    navigate(text.trim() ? '/explore?q=' + encodeURIComponent(text.trim()) : '/explore');
  };

  return (
    <AppShell mobileHeader={false}>
      <div className="page explore">
        <h1 className="page-title">Explore</h1>
        {!desktop && (
          <form className="search-field" role="search" onSubmit={submit}>
            <Icon name="search" size={20} />
            <label htmlFor="explore-search" className="sr-only">Search</label>
            <input id="explore-search" type="search" placeholder="PAN card, SIP, interview…" value={text} onChange={(e) => setText(e.target.value)} />
            <button type="button" className="icon-btn" aria-label="Search by voice" onClick={() => setText('pan card')}><Icon name="mic" size={20} /></button>
          </form>
        )}

        <div className="topic-tiles">
          {TOPICS.map((x) => (
            <button
              key={x.slug}
              type="button"
              className={'topic-tile' + (!q && x.slug === current ? ' on' : '')}
              onClick={() => navigate('/explore/' + x.slug)}
              aria-pressed={!q && x.slug === current}
            >
              <span className="topic-dot" style={{ background: x.color }}>{x.glyph}</span>
              <span>{x.name}</span>
            </button>
          ))}
        </div>

        <section className="stack">
          <h2 className="rail-title">
            {q ? <>Results for “{q}”</> : <>Popular in {t?.name}</>}
          </h2>
          {shows.loading && !shows.data && <SkeletonRails rails={1} />}
          {shows.error && <ErrorBox message={shows.error} onRetry={shows.reload} />}
          {shows.data && shows.data.length === 0 && (
            <div className="empty">
              <p>No shows match “{q}”. Try a shorter word, like “PAN” or “SIP”.</p>
            </div>
          )}
          <div className="poster-grid">
            {shows.data?.map((s) => <PosterLink key={s.id} show={s} size="fill" meta={`${s.episodeCount} episodes${s.access === 'free' ? ' · Free' : ''}`} />)}
          </div>
        </section>
      </div>
    </AppShell>
  );
}
